import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { DateInput } from '@blueprintjs/datetime';
import moment from 'moment-timezone';
import { Choice, FieldError } from 'components/FormBits';

// When a job happens, in the job form (pages/jobs/add_job.js): the day, the
// arrival time and how long it takes, with the day drawn as a bar so the
// visit's place in it is obvious. Everything goes through the form's own
// handlers: onStartChange(moment) (the form keeps the length) and
// onEndChange(moment). Styles: .wp-* in styles/job-form.scss.

const STEP = 15; // minutes between times in the lists
const DAY_START = 6; // the bar shows 6 AM…
const DAY_END = 20; // …to 8 PM, widened if the visit is outside it

const DURATIONS = [
  { value: 30, label: '30 min' },
  { value: 60, label: '1 hr' },
  { value: 120, label: '2 hrs' },
  { value: 240, label: '4 hrs' },
];

const minutesOfDay = (m) => m.hours() * 60 + m.minutes();

export const formatLength = (minutes) => {
  if (!(minutes > 0)) return '';
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  const parts = [];
  if (days) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
  if (hours) parts.push(`${hours} ${hours === 1 ? 'hr' : 'hrs'}`);
  if (mins) parts.push(`${mins} min`);
  return parts.join(' ');
};

// Times of day every 15 minutes, plus `extra` (a time off the grid) if given.
const timesOfDay = (extra) => {
  const list = [];
  for (let m = 0; m < 24 * 60; m += STEP) list.push(m);
  if (extra !== undefined && !list.includes(extra)) list.push(extra);
  return list.sort((a, b) => a - b);
};

const timeLabel = (minutes) => moment().startOf('day').add(minutes, 'minutes').format('h:mm A');

class WhenPicker extends Component {
  state = { otherDay: false };

  get start() {
    return this.props.start ? moment(this.props.start) : moment();
  }

  get end() {
    return this.props.end ? moment(this.props.end) : moment(this.start).add(1, 'hour');
  }

  get length() {
    return Math.round(this.end.diff(this.start, 'minutes', true));
  }

  // Same time, different day (the form keeps the length).
  moveToDay(day) {
    const start = this.start;
    const next = moment(day).hours(start.hours()).minutes(start.minutes()).seconds(0).milliseconds(0);
    this.props.onStartChange(next);
  }

  setArrival(minutes) {
    const next = moment(this.start).startOf('day').add(minutes, 'minutes');
    this.props.onStartChange(next);
  }

  setEndTime(minutes) {
    const end = this.end;
    this.props.onEndChange(moment(end).startOf('day').add(minutes, 'minutes'));
  }

  setEndDay(day) {
    const end = this.end;
    this.props.onEndChange(moment(day).hours(end.hours()).minutes(end.minutes()).seconds(0).milliseconds(0));
  }

  renderDay() {
    const start = this.start;
    const today = moment().startOf('day');
    const quick = [0, 1, 2].map((offset) => moment(today).add(offset, 'days'));
    const onQuickDay = quick.some((day) => day.isSame(start, 'day'));
    return (
      <div className="wp-row">
        <span className="fb-label" id="wp-day-label">Day</span>
        <div className="wp-days" role="group" aria-labelledby="wp-day-label">
          {quick.map((day, index) => {
            const selected = day.isSame(start, 'day');
            const label = index === 0 ? 'Today' : index === 1 ? 'Tomorrow' : day.format('ddd, MMM D');
            return (
              <button
                key={day.format('YYYY-MM-DD')}
                type="button"
                className={`fb-choice__option${selected ? ' is-selected' : ''}`}
                aria-pressed={selected}
                onClick={() => this.moveToDay(day)}
              >
                {label}
              </button>
            );
          })}
          <div className={`wp-date${!onQuickDay ? ' is-selected' : ''}`}>
            <DateInput
              inputProps={{ 'aria-label': 'Pick another day', placeholder: 'Pick a date' }}
              value={onQuickDay ? null : start.toDate()}
              onChange={(date) => date && this.moveToDay(date)}
              formatDate={(date) => moment(date).format('ddd, MMM D, YYYY')}
              parseDate={(text) => (moment(text, 'ddd, MMM D, YYYY').isValid() ? moment(text, 'ddd, MMM D, YYYY').toDate() : null)}
              maxDate={moment().add(5, 'years').toDate()}
              minDate={moment().subtract(5, 'years').toDate()}
              canClearSelection={false}
              popoverProps={{ minimal: true }}
            />
          </div>
        </div>
      </div>
    );
  }

  renderTimes() {
    const start = this.start;
    const end = this.end;
    const startMinutes = minutesOfDay(start);
    const sameDay = end.isSame(start, 'day');
    const multiDay = this.state.otherDay || !sameDay;
    const endMinutes = minutesOfDay(end);
    // On the same day, only times after the arrival make sense for the end.
    const endChoices = timesOfDay(endMinutes).filter((m) => multiDay || m > startMinutes);
    return (
      <div className="wp-times">
        <div className="wp-time">
          <label className="fb-label" htmlFor="wp-arrive">Arrives</label>
          <div className="bp3-select bp3-fill">
            <select id="wp-arrive" value={startMinutes} onChange={(event) => this.setArrival(Number(event.target.value))}>
              {timesOfDay(startMinutes).map((m) => (
                <option key={m} value={m}>{timeLabel(m)}</option>
              ))}
            </select>
          </div>
        </div>
        <div className={`wp-time${this.props.error ? ' has-error' : ''}`} id={this.props.endId}>
          <label className="fb-label" htmlFor="wp-end">Ends</label>
          <div className="bp3-select bp3-fill">
            <select id="wp-end" value={endMinutes} onChange={(event) => this.setEndTime(Number(event.target.value))}>
              {endChoices.map((m) => {
                const length = multiDay ? null : m - startMinutes;
                return (
                  <option key={m} value={m}>
                    {timeLabel(m)}{length ? ` (${formatLength(length)})` : ''}
                  </option>
                );
              })}
            </select>
          </div>
          {multiDay ? (
            <div className="wp-endday">
              <DateInput
                inputProps={{ 'aria-label': 'Day the job ends' }}
                value={end.toDate()}
                minDate={start.toDate()}
                maxDate={moment(start).add(1, 'year').toDate()}
                onChange={(date) => date && this.setEndDay(date)}
                formatDate={(date) => moment(date).format('ddd, MMM D')}
                parseDate={(text) => (moment(text, 'ddd, MMM D').isValid() ? moment(text, 'ddd, MMM D').toDate() : null)}
                canClearSelection={false}
                popoverProps={{ minimal: true }}
              />
            </div>
          ) : (
            <button type="button" className="wp-link" onClick={() => this.setState({ otherDay: true })}>
              Ends on another day
            </button>
          )}
          <FieldError>{this.props.error}</FieldError>
        </div>
      </div>
    );
  }

  // The day as a bar with the visit on it (and "now" when it's today).
  renderDayBar() {
    const start = this.start;
    const end = this.end;
    const sameDay = end.isSame(start, 'day');
    const from = minutesOfDay(start);
    const to = sameDay ? minutesOfDay(end) : 24 * 60;
    const barStart = Math.min(DAY_START * 60, Math.floor(from / 60) * 60);
    const barEnd = Math.max(DAY_END * 60, Math.ceil(to / 60) * 60);
    const span = barEnd - barStart;
    const left = ((from - barStart) / span) * 100;
    const width = Math.max(((to - from) / span) * 100, 1.5);
    const now = moment();
    const showNow = now.isSame(start, 'day') && minutesOfDay(now) >= barStart && minutesOfDay(now) <= barEnd;
    const hours = [];
    for (let h = barStart / 60; h <= barEnd / 60; h += 1) hours.push(h);
    const tickEvery = span > 16 * 60 ? 4 : 2;
    return (
      <div className="wp-bar" aria-hidden="true">
        <div className="wp-bar__track">
          {hours.map((h) => (
            <span key={h} className="wp-bar__hour" style={{ left: `${((h * 60 - barStart) / span) * 100}%` }} />
          ))}
          {showNow && <span className="wp-bar__now" style={{ left: `${((minutesOfDay(now) - barStart) / span) * 100}%` }} />}
          <span className={`wp-bar__visit${sameDay ? '' : ' is-open-ended'}`} style={{ left: `${left}%`, width: `${width}%` }} />
        </div>
        <div className="wp-bar__labels">
          {hours.filter((h) => (h - barStart / 60) % tickEvery === 0).map((h) => (
            <span key={h} style={{ left: `${((h * 60 - barStart) / span) * 100}%` }}>
              {moment().startOf('day').hours(h).format('h A')}
            </span>
          ))}
        </div>
      </div>
    );
  }

  render() {
    const start = this.start;
    const end = this.end;
    const length = this.length;
    const sameDay = end.isSame(start, 'day');
    const summary = sameDay
      ? `${start.format('dddd, MMM D')}, ${start.format('h:mm A')} to ${end.format('h:mm A')}`
      : `${start.format('ddd, MMM D, h:mm A')} to ${end.format('ddd, MMM D, h:mm A')}`;
    const durationValue = DURATIONS.some((d) => d.value === length) ? length : null;
    return (
      <div className="wp">
        {this.renderDay()}
        {this.renderTimes()}
        <Choice
          label="How long"
          name="How long"
          value={durationValue}
          onChange={(minutes) => {
            this.setState({ otherDay: false });
            this.props.onEndChange(moment(start).add(minutes, 'minutes'));
          }}
          options={DURATIONS}
        />
        <div className="wp-summary" aria-live="polite">
          {this.renderDayBar()}
          <p className="wp-summary__text">
            {summary}
            {length > 0 && <span className="wp-summary__length">{formatLength(length)}</span>}
          </p>
        </div>
      </div>
    );
  }
}

WhenPicker.propTypes = {
  start: PropTypes.any,
  end: PropTypes.any,
  onStartChange: PropTypes.func.isRequired,
  onEndChange: PropTypes.func.isRequired,
  error: PropTypes.node,
  endId: PropTypes.string,
};

export default WhenPicker;

// Visit times for a service agreement (pages/jobs/persist_service_agreement):
// arrival and end time of day, plus duration chips. Moving the arrival keeps
// the length. onChange({ start, end }) with Dates on the same day as before.
export class VisitTimes extends Component {
  // No times stored yet (a new agreement): store the ones shown.
  componentDidMount() {
    if (!this.props.start || !this.props.end) {
      this.props.onChange({ start: this.start.toDate(), end: this.end.toDate() });
    }
  }

  get start() {
    return this.props.start ? moment(this.props.start) : moment().startOf('hour').add(1, 'hour');
  }

  get end() {
    return this.props.end ? moment(this.props.end) : moment(this.start).add(1, 'hour');
  }

  get length() {
    const length = Math.round(this.end.diff(this.start, 'minutes', true));
    return length > 0 ? length : 60;
  }

  setArrival(minutes) {
    const start = moment(this.start).startOf('day').add(minutes, 'minutes');
    this.props.onChange({ start: start.toDate(), end: moment(start).add(this.length, 'minutes').toDate() });
  }

  setEnd(minutes) {
    this.props.onChange({ start: this.start.toDate(), end: moment(this.start).startOf('day').add(minutes, 'minutes').toDate() });
  }

  setLength(minutes) {
    this.props.onChange({ start: this.start.toDate(), end: moment(this.start).add(minutes, 'minutes').toDate() });
  }

  render() {
    const startMinutes = minutesOfDay(this.start);
    const endMinutes = minutesOfDay(this.end);
    const length = Math.round(this.end.diff(this.start, 'minutes', true));
    return (
      <div className="wp wp--visit">
        <div className="wp-times">
          <div className="wp-time">
            <label className="fb-label" htmlFor="visit-arrive">Arrives</label>
            <div className="bp3-select bp3-fill">
              <select id="visit-arrive" value={startMinutes} onChange={(event) => this.setArrival(Number(event.target.value))}>
                {timesOfDay(startMinutes).map((m) => (
                  <option key={m} value={m}>{timeLabel(m)}</option>
                ))}
              </select>
            </div>
          </div>
          <div className={`wp-time${this.props.error ? ' has-error' : ''}`}>
            <label className="fb-label" htmlFor="visit-end">Leaves</label>
            <div className="bp3-select bp3-fill">
              <select id="visit-end" value={endMinutes} onChange={(event) => this.setEnd(Number(event.target.value))}>
                {timesOfDay(endMinutes).filter((m) => m > startMinutes || m === endMinutes).map((m) => (
                  <option key={m} value={m}>
                    {timeLabel(m)}{m > startMinutes ? ` (${formatLength(m - startMinutes)})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <FieldError>{this.props.error}</FieldError>
          </div>
        </div>
        <Choice
          label="How long"
          name="Visit length"
          value={DURATIONS.some((d) => d.value === length) ? length : null}
          onChange={(minutes) => this.setLength(minutes)}
          options={DURATIONS}
        />
      </div>
    );
  }
}

VisitTimes.propTypes = {
  start: PropTypes.any,
  end: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.node,
};
